import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
  Stack,
  LinearProgress,
  Alert
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  PieChart as PieChartIcon,
  BarChart3,
  FileText,
  Download,
  Calendar,
  Filter,
  Building,
  Receipt,
  Eye,
  Printer,
  Mail,
  ArrowUpRight,
  ArrowDownRight,
  Info,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AccountingService from '../services/accountingService';
import { unitApi, bookingApi } from '../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import HierarchicalUnitSelector from './HierarchicalUnitSelector';

// Recharts imports with fallback handling
let LineChart: any, Line: any, XAxis: any, YAxis: any, CartesianGrid: any, Tooltip: any, ResponsiveContainer: any;
let BarChart: any, Bar: any, PieChart: any, Cell: any, Pie: any;

try {
  const recharts = require('recharts');
  ({ LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Cell, Pie } = recharts);
} catch (error) {
  console.warn('Recharts library not available, charts will be disabled');
}

interface Property {
  id: number;
  name: string;
  currency_id?: number;
  units?: Unit[];
}

interface Currency {
  id: number;
  name: string;
  symbol: string;
  code: string;
}

interface Unit {
  id: number;
  property_id: number;
  unit_number?: string;
  type: string; // Dynamic unit type
  name: string;
  bedrooms?: number;
  bathrooms?: number;
  square_feet?: number;
  rent_amount?: number;
  status?: 'vacant' | 'occupied' | 'maintenance';
  description?: string;
  rental_types?: ('Hour' | '3 Hours' | 'Daily' | 'Monthly' | 'Yearly')[];
  images?: string[];
  videos?: string[];
  created_at?: string;
  updated_at?: string;
}

interface FinancialReportsProps {
  properties: Property[];
  currencies: Currency[];
  selectedPropertyId?: number;
  onPropertyChange: (propertyId?: number) => void;
  selectedUnitId?: number;
  onUnitChange?: (unitId?: number) => void;
}

interface ReportData {
  income_statement: {
    revenue: number;
    expenses: number;
    property_expenses: number;
    unit_expenses: number;
    net_income: number;
    outstanding_revenue?: number; // Unpaid revenue (bookings not yet paid)
    outstanding_expenses?: number; // Unpaid expenses (bills not yet paid)
    revenue_breakdown: Array<{ category: string; amount: number; }>;
    expense_breakdown: Array<{ category: string; amount: number; }>;
    property_expense_breakdown: Array<{ category: string; amount: number; }>;
    unit_expense_breakdown: Array<{ category: string; amount: number; }>;
  };
  balance_sheet: {
    assets: number;
    liabilities: number;
    equity: number;
    asset_breakdown: Array<{ category: string; amount: number; }>;
    liability_breakdown: Array<{ category: string; amount: number; }>;
  };
  cash_flow: {
    operating: number;
    investing: number;
    financing: number;
    net_cash_flow: number;
  };
  monthly_trends: Array<{ month: string; revenue: number; expenses: number; profit: number; }>;
}

const COLORS = ['#1976d2', '#9c27b0', '#ff9800', '#4caf50', '#f44336', '#00bcd4', '#795548', '#607d8b'];

const FinancialReports: React.FC<FinancialReportsProps> = ({
  properties,
  currencies,
  selectedPropertyId,
  onPropertyChange,
  selectedUnitId,
  onUnitChange
}) => {
  const { t } = useTranslation();
  const [activeReport, setActiveReport] = useState('overview');
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Start of year
    end_date: new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0] // End of year
  });
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [usingMockData, setUsingMockData] = useState(false);
  const [availableUnits, setAvailableUnits] = useState<Unit[]>([]); // Units for filter dropdown
  
  // State for expandable sections in Income Statement
  const [expandedSections, setExpandedSections] = useState({
    revenue: true,
    expenses: true,
    unitExpenses: true
  });

  // Empty data generator for when no transactions exist
  const generateEmptyReportData = (): ReportData => {
    return {
      income_statement: {
        revenue: 0,
        expenses: 0,
        property_expenses: 0,
        unit_expenses: 0,
        net_income: 0,
        outstanding_revenue: 0,
        outstanding_expenses: 0,
        revenue_breakdown: [],
        expense_breakdown: [],
        property_expense_breakdown: [],
        unit_expense_breakdown: []
      },
      balance_sheet: {
        assets: 0,
        liabilities: 0,
        equity: 0,
        asset_breakdown: [],
        liability_breakdown: []
      },
      cash_flow: {
        operating: 0,
        investing: 0,
        financing: 0,
        net_cash_flow: 0
      },
      monthly_trends: []
    };
  };

  useEffect(() => {
    loadReportData();
  }, [selectedPropertyId, selectedUnitId, dateRange]);

  // Load all units once when component mounts
  useEffect(() => {
    loadAvailableUnits();
  }, []);

  // Listen for transaction commits to refresh financial data
  useEffect(() => {
    const handleTransactionCommitted = () => {
      console.log('Transaction committed, refreshing financial reports...');
      loadReportData();
    };

    window.addEventListener('transactionCommitted', handleTransactionCommitted);
    return () => window.removeEventListener('transactionCommitted', handleTransactionCommitted);
  }, []);

  const loadAvailableUnits = async () => {
    try {
      console.log('Loading all available units for hierarchical selector');
      
      // Load all units for hierarchical selector
      const unitsData = await unitApi.getAll();
      setAvailableUnits(unitsData || []);
      console.log('Available units loaded from API:', unitsData);
    } catch (error: any) {
      console.error('Error loading available units:', error);
      setAvailableUnits([]);
    }
  };

  // Toggle function for expandable sections in Income Statement
  // Allows users to expand/collapse REVENUE, EXPENSES, and UNIT EXPENSES sections
  const toggleSection = (section: 'revenue' | 'expenses' | 'unitExpenses') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const loadReportData = async () => {
    setLoading(true);
    console.log('🔄 loadReportData called with:', {
      selectedPropertyId,
      selectedUnitId,
      dateRange,
      propertiesCount: properties.length
    });
    
    // If no property is selected, use empty data
    if (!selectedPropertyId) {
      console.log('No property selected, using empty data');
      const emptyData = generateEmptyReportData();
      setReportData(emptyData);
      setUsingMockData(false);
      setLoading(false);
      return;
    }
    
    try {
      // Check authentication status first
      const token = localStorage.getItem('token');
      console.log('Authentication token present:', !!token);
      
      console.log('Loading financial overview from API...', {
        property_id: selectedPropertyId,
        unit_id: selectedUnitId,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      });
      
      if (!token) {
        const propertyName = properties.find(p => p.id === selectedPropertyId)?.name || `Property ${selectedPropertyId}`;
        console.warn('No authentication token found, but proceeding with API call anyway for development...');
        // For development, we'll skip token check and try the API anyway
        // In production, this should fall back to mock data
      }
      
      // Use income statement report API to get real data with separated expenses
      const reportData = await AccountingService.generateReport({
        property_id: selectedPropertyId,
        unit_id: selectedUnitId,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        report_type: 'income_statement'
      });

      console.log('Raw API response:', reportData);
      console.log('=== DEBUGGING INCOME STATEMENT API ===');
      console.log('Request parameters:', {
        property_id: selectedPropertyId,
        unit_id: selectedUnitId,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        report_type: 'income_statement'
      });
      console.log('Response structure:', typeof reportData, Object.keys(reportData || {}));
      console.log('🏠 Property Expenses Array:', reportData?.property_expenses);
      console.log('🏢 Unit Expenses Array:', reportData?.unit_expenses);
      console.log('💰 Total Property Expenses:', reportData?.total_property_expenses);
      console.log('🏠 Total Unit Expenses:', reportData?.total_unit_expenses);
      console.log('📊 Total Revenue:', reportData?.total_revenue);
      console.log('========================================');
      
      // Check if we got meaningful data from the API
      const hasRealData = reportData && (
        (reportData?.total_revenue > 0) ||
        (reportData?.total_expenses > 0) ||
        (reportData?.property_expenses && reportData.property_expenses.length > 0) ||
        (reportData?.unit_expenses && reportData.unit_expenses.length > 0)
      );

      if (hasRealData) {
        console.log('Using real data from API');
        
        // Validate the structure and set defaults if needed
        const validatedData: ReportData = {
          income_statement: {
            revenue: reportData?.total_revenue || 0,
            expenses: reportData?.total_expenses || 0,
            property_expenses: reportData?.total_property_expenses || 0,
            unit_expenses: reportData?.total_unit_expenses || 0,
            net_income: reportData?.net_income || 0,
            outstanding_revenue: reportData?.outstanding_revenue || 0,
            outstanding_expenses: reportData?.outstanding_expenses || 0,
            revenue_breakdown: reportData?.revenue || [],
            expense_breakdown: reportData?.expenses || [],
            property_expense_breakdown: reportData?.property_expenses || [],
            unit_expense_breakdown: reportData?.unit_expenses || []
          },
          balance_sheet: {
            assets: 0, // Income statement doesn't include balance sheet data
            liabilities: 0,
            equity: 0,
            asset_breakdown: [],
            liability_breakdown: []
          },
          cash_flow: {
            operating: 0, // Income statement doesn't include cash flow data
            investing: 0,
            financing: 0,
            net_cash_flow: 0
          },
          monthly_trends: []
        };

        // Always calculate outstanding amounts from booking data
        try {
          console.log('Calculating outstanding amounts from booking data...');
          
          // Method 1: Get bookings to calculate outstanding revenue
          const allBookings = await bookingApi.getAll();
          
          // Filter bookings by property and unit if specified
          let filteredBookings = Array.isArray(allBookings) ? allBookings : [];
          if (selectedPropertyId) {
            filteredBookings = filteredBookings.filter((b: any) => b.property_id === selectedPropertyId);
          }
          if (selectedUnitId) {
            filteredBookings = filteredBookings.filter((b: any) => b.unit_id === selectedUnitId);
          }
          
          // Filter by date range (check if booking overlaps with the reporting period)
          const startDate = new Date(dateRange.start_date);
          const endDate = new Date(dateRange.end_date);
          filteredBookings = filteredBookings.filter((b: any) => {
            const bookingStart = new Date(b.start_date);
            const bookingEnd = new Date(b.end_date);
            // Check if booking overlaps with reporting period
            return bookingStart <= endDate && bookingEnd >= startDate;
          });
          
          // Calculate outstanding revenue from bookings
          let outstandingRevenue = 0;
          filteredBookings.forEach((booking: any) => {
            const remainingAmount = booking.remaining_amount || 0;
            
            // Add to outstanding if there's an unpaid amount
            if (remainingAmount > 0) {
              outstandingRevenue += remainingAmount;
              console.log(`Outstanding: Booking ${booking.booking_number || booking.id} - €${remainingAmount}`);
            }
          });
          
          // Method 2: For outstanding expenses, we'll use a simplified approach
          // In a real application, you might fetch bills/expenses that are unpaid
          let outstandingExpenses = 0;
          // For now, we can estimate or set to 0 since we don't have a bills API
          // You could implement a similar approach with an expenses/bills API

          console.log(`Total Outstanding Revenue: €${outstandingRevenue} from ${filteredBookings.filter((b: any) => (b.remaining_amount || 0) > 0).length} unpaid bookings`);

          // Update the validated data with calculated outstanding amounts
          validatedData.income_statement.outstanding_revenue = outstandingRevenue;
          validatedData.income_statement.outstanding_expenses = outstandingExpenses;

        } catch (outstandingError) {
          console.error('Error calculating outstanding amounts:', outstandingError);
          // Set defaults if calculation fails
          validatedData.income_statement.outstanding_revenue = 0;
          validatedData.income_statement.outstanding_expenses = 0;
        }

        setReportData(validatedData);
        setUsingMockData(false);
      } else {
        console.log('No meaningful data from API, using empty data');
        const emptyData = generateEmptyReportData();
        setReportData(emptyData);
        setUsingMockData(false);
      }
    } catch (error: any) {
      console.error('Error loading financial overview:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      console.error('Full error object:', error);
      
      // Check if it's an authentication error
      if (error.response?.status === 401 || error.response?.status === 302) {
        console.warn('Authentication error detected, user needs to log in');
      }
      
      // Check if it's a validation error
      if (error.response?.status === 422) {
        console.error('Validation error:', error.response?.data?.errors);
      }
      
      // Fallback to empty data if API fails
      const propertyName = properties.find(p => p.id === selectedPropertyId)?.name || `Property ${selectedPropertyId}`;
      console.log('Falling back to empty data for:', propertyName);
      console.warn('API Error - Using empty data. Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      const emptyData = generateEmptyReportData();
      setReportData(emptyData);
      setUsingMockData(false);
    } finally {
      setLoading(false);
    }
  };

  // Function to calculate unit expenses from transactions
  const calculateUnitExpenses = async (propertyId?: number, startDate?: string, endDate?: string): Promise<number> => {
    try {
      if (!propertyId) return 0;
      
      // Get all transactions for the property within the date range
      const transactionsData = await AccountingService.getTransactions({
        property_id: propertyId,
        start_date: startDate,
        end_date: endDate
      });
      
      const transactions = transactionsData.data || [];
      
      // Filter for expense transactions that have unit information
      const unitExpenses = transactions.filter((transaction: any) => 
        transaction.transaction_type === 'EXPENSE' && 
        transaction.unit_id && 
        transaction.unit_number && 
        transaction.unit_name
      );
      
      // Sum up the unit expenses
      const totalUnitExpenses = unitExpenses.reduce((sum: number, transaction: any) => {
        const amount = Number(transaction.total_amount) || 0;
        return sum + amount;
      }, 0);
      
      console.log(`Unit expenses for property ${propertyId}:`, {
        totalUnitExpenses,
        unitExpenseCount: unitExpenses.length,
        totalTransactions: transactions.length,
        sampleAmounts: unitExpenses.slice(0, 3).map(t => ({
          description: t.description,
          total_amount: t.total_amount,
          type: typeof t.total_amount,
          parsed: Number(t.total_amount)
        }))
      });
      
      return totalUnitExpenses;
    } catch (error) {
      console.error('Error calculating unit expenses:', error);
      return 0;
    }
  };

  const formatCurrency = (amount: number) => {
    const selectedProperty = properties.find(p => p.id === selectedPropertyId);
    const currency = currencies.find(c => c.id === selectedProperty?.currency_id) || currencies[0];
    return `${currency?.symbol || '$'}${amount.toLocaleString()}`;
  };

  // Helper function to get hierarchical unit name by unit ID
  const getUnitHierarchyNameById = (unitId: number): string => {
    const unit = availableUnits?.find((u: any) => u.id === unitId);
    if (!unit) return 'Unknown Unit';
    
    // Build parent path using units array
    const buildParentPath = (currentUnit: any): string => {
      const parent = availableUnits?.find((u: any) => u.id === currentUnit.parent_unit_id);
      if (!parent) return currentUnit.unit_number;
      
      const parentPath = buildParentPath(parent);
      return `${parentPath} → ${currentUnit.unit_number}`;
    };
    
    return buildParentPath(unit);
  };

  const StatCard = ({ 
    title, 
    value, 
    change, 
    icon: Icon, 
    color = 'primary',
    subtitle
  }: {
    title: string;
    value: string;
    change?: string;
    icon: any;
    color?: 'primary' | 'success' | 'error' | 'warning';
    subtitle?: string;
  }) => (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          },
          transition: 'all 0.3s ease'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${
                  color === 'success' ? '#4caf50, #2e7d32' :
                  color === 'error' ? '#f44336, #c62828' :
                  color === 'warning' ? '#ff9800, #f57c00' :
                  '#1976d2, #9c27b0'
                })`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}
            >
              <Icon size={28} />
            </Box>
            {change && (
              <Chip
                label={change}
                size="small"
                icon={change.startsWith('+') ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                sx={{
                  bgcolor: change.startsWith('+') ? '#e8f5e8' : '#ffebee',
                  color: change.startsWith('+') ? '#2e7d32' : '#c62828',
                  fontWeight: 'bold'
                }}
              />
            )}
          </Box>
          
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {value}
          </Typography>
          
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {title}
          </Typography>
          
          {subtitle && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
              {subtitle}
            </Typography>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );

  const renderOverview = () => {
    if (!reportData) return null;

    // Show message if no property is selected
    if (!selectedPropertyId) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {t('select_property_to_view_reports')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the property selector above to choose a property and view its financial data.
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {/* Key Metrics */}
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            Key Financial Metrics
          </Typography>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Total Revenue"
            value={formatCurrency(reportData.income_statement.revenue)}
            change="+12.5%"
            icon={TrendingUp}
            color="success"
            subtitle="vs. last period"
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Total Expenses"
            value={formatCurrency(reportData.income_statement.expenses)}
            change="+8.2%"
            icon={TrendingDown}
            color="error"
            subtitle="vs. last period"
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Net Income"
            value={formatCurrency(reportData.income_statement.net_income)}
            change="+18.7%"
            icon={DollarSign}
            color="primary"
            subtitle="vs. last period"
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Occupancy Rate"
            value="92.5%"
            change="+2.1%"
            icon={Building}
            color="warning"
            subtitle="vs. last period"
          />
        </Grid>

        {/* Revenue Trend Chart */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                Revenue vs Expenses Trend
              </Typography>
              <Box sx={{ height: 300 }}>
                {ResponsiveContainer && LineChart ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={reportData.monthly_trends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="expenses" 
                        stroke="#f44336" 
                        strokeWidth={3}
                        dot={{ fill: '#f44336', strokeWidth: 2, r: 5 }}
                        name="Expenses"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    bgcolor: '#f5f5f5',
                    borderRadius: 2
                  }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Chart visualization unavailable
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Breakdown Pie Chart */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                Revenue Breakdown
              </Typography>
              <Box sx={{ height: 300 }}>
                {ResponsiveContainer && PieChart ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={reportData.income_statement.revenue_breakdown}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="amount"
                      >
                        {reportData.income_statement.revenue_breakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: any) => [formatCurrency(value), 'Amount']}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    bgcolor: '#f5f5f5',
                    borderRadius: 2
                  }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Chart visualization unavailable
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 2 }}>
                {reportData.income_statement.revenue_breakdown.map((item, index) => (
                  <Box key={item.category} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: 1,
                        bgcolor: COLORS[index % COLORS.length],
                        mr: 1
                      }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
                      {item.category}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(item.amount)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderIncomeStatement = () => {
    if (!reportData) return null;

    // Show message if no property is selected
    if (!selectedPropertyId) {
      return (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
              {t('income_statement')}
            </Typography>
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                {t('select_property_to_view_income')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a property from the property selector to see detailed financial information.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {t('income_statement')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                startIcon={<Download size={16} />} 
                variant="outlined" 
                size="small"
                data-export-pdf="income-statement"
                onClick={exportIncomeStatementToPDF}
              >
                Export PDF
              </Button>
              <Button startIcon={<Mail size={16} />} variant="outlined" size="small">
                Email
              </Button>
            </Box>
          </Box>

          <div id="income-statement-content">
            <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Account</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>%</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow 
                  onClick={() => toggleSection('revenue')} 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                    backgroundColor: expandedSections.revenue ? 'rgba(25, 118, 210, 0.02)' : 'transparent'
                  }}
                >
                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.1rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box 
                        sx={{ 
                          transition: 'transform 0.2s ease',
                          transform: expandedSections.revenue ? 'rotate(0deg)' : 'rotate(-90deg)'
                        }}
                      >
                        <ChevronDown size={20} />
                      </Box>
                      REVENUE
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {formatCurrency(reportData.income_statement.revenue)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    100%
                  </TableCell>
                </TableRow>
                {expandedSections.revenue && reportData.income_statement.revenue_breakdown.map((item) => (
                  <TableRow key={item.category}>
                    <TableCell sx={{ pl: 4 }}>{item.category}</TableCell>
                    <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                    <TableCell align="right">
                      {((item.amount / reportData.income_statement.revenue) * 100).toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
                
                {/* Property Expenses Section */}
                <TableRow 
                  onClick={() => toggleSection('expenses')} 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.04)' },
                    backgroundColor: expandedSections.expenses ? 'rgba(244, 67, 54, 0.02)' : 'transparent'
                  }}
                >
                  <TableCell sx={{ pt: 3, fontWeight: 'bold', color: 'error.main', fontSize: '1.1rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box 
                        sx={{ 
                          transition: 'transform 0.2s ease',
                          transform: expandedSections.expenses ? 'rotate(0deg)' : 'rotate(-90deg)'
                        }}
                      >
                        <ChevronDown size={20} />
                      </Box>
                      EXPENSES
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ pt: 3, fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {formatCurrency(reportData.income_statement.property_expenses)}
                  </TableCell>
                  <TableCell align="right" sx={{ pt: 3, fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {reportData.income_statement.revenue > 0
                      ? `${((reportData.income_statement.property_expenses / reportData.income_statement.revenue) * 100).toFixed(1)}%`
                      : '0.0%'
                    }
                  </TableCell>
                </TableRow>
                {expandedSections.expenses && reportData.income_statement.property_expense_breakdown.map((item) => (
                  <TableRow key={`property-${item.category}`}>
                    <TableCell sx={{ pl: 4 }}>{item.category}</TableCell>
                    <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                    <TableCell align="right">
                      {reportData.income_statement.revenue > 0 
                        ? `${((item.amount / reportData.income_statement.revenue) * 100).toFixed(1)}%`
                        : '0.0%'
                      }
                    </TableCell>
                  </TableRow>
                ))}
                
                {/* Unit Expenses Section */}
                <TableRow 
                  onClick={() => toggleSection('unitExpenses')} 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.04)' },
                    backgroundColor: expandedSections.unitExpenses ? 'rgba(255, 152, 0, 0.02)' : 'transparent'
                  }}
                >
                  <TableCell sx={{ pt: 2, fontWeight: 'bold', color: 'warning.main', fontSize: '1.05rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box 
                        sx={{ 
                          transition: 'transform 0.2s ease',
                          transform: expandedSections.unitExpenses ? 'rotate(0deg)' : 'rotate(-90deg)'
                        }}
                      >
                        <ChevronDown size={20} />
                      </Box>
                      UNIT EXPENSES
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ pt: 2, fontWeight: 'bold', fontSize: '1.05rem' }}>
                    {formatCurrency(reportData.income_statement.unit_expenses)}
                  </TableCell>
                  <TableCell align="right" sx={{ pt: 2, fontWeight: 'bold', fontSize: '1.05rem' }}>
                    {reportData.income_statement.revenue > 0 
                      ? `${((reportData.income_statement.unit_expenses / reportData.income_statement.revenue) * 100).toFixed(1)}%`
                      : '0.0%'
                    }
                  </TableCell>
                </TableRow>
                {expandedSections.unitExpenses && reportData.income_statement.unit_expense_breakdown.map((item) => (
                  <TableRow key={`unit-${item.category}`}>
                    <TableCell sx={{ pl: 4 }}>{item.category}</TableCell>
                    <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                    <TableCell align="right">
                      {reportData.income_statement.revenue > 0 
                        ? `${((item.amount / reportData.income_statement.revenue) * 100).toFixed(1)}%`
                        : '0.0%'
                      }
                    </TableCell>
                  </TableRow>
                ))}
                
                <TableRow>
                  <TableCell 
                    sx={{ 
                      pt: 3, 
                      fontWeight: 'bold', 
                      fontSize: '1.2rem',
                      borderTop: '2px solid #e0e0e0',
                      color: reportData.income_statement.net_income >= 0 ? 'success.main' : 'error.main'
                    }}
                  >
                    NET INCOME
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      pt: 3, 
                      fontWeight: 'bold', 
                      fontSize: '1.2rem',
                      borderTop: '2px solid #e0e0e0',
                      color: reportData.income_statement.net_income >= 0 ? 'success.main' : 'error.main'
                    }}
                  >
                    {formatCurrency(reportData.income_statement.net_income)}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      pt: 3, 
                      fontWeight: 'bold', 
                      fontSize: '1.2rem',
                      borderTop: '2px solid #e0e0e0',
                      color: reportData.income_statement.net_income >= 0 ? 'success.main' : 'error.main'
                    }}
                  >
                    {((reportData.income_statement.net_income / reportData.income_statement.revenue) * 100).toFixed(1)}%
                  </TableCell>
                </TableRow>

                {/* Outstanding Amounts Section */}
                <TableRow>
                  <TableCell colSpan={3} sx={{ pt: 4, pb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                      OUTSTANDING AMOUNTS
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Outstanding Revenue */}
                <TableRow>
                  <TableCell sx={{ pl: 3, color: 'text.secondary' }}>
                    Outstanding Revenue (Unpaid Bookings)
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                    {formatCurrency(reportData.income_statement.outstanding_revenue || 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {reportData.income_statement.revenue > 0 
                      ? (((reportData.income_statement.outstanding_revenue || 0) / reportData.income_statement.revenue) * 100).toFixed(1)
                      : '0.0'
                    }%
                  </TableCell>
                </TableRow>

                {/* Outstanding Expenses */}
                <TableRow>
                  <TableCell sx={{ pl: 3, color: 'text.secondary' }}>
                    Outstanding Expenses (Unpaid Bills)
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                    {formatCurrency(reportData.income_statement.outstanding_expenses || 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {reportData.income_statement.revenue > 0 
                      ? (((reportData.income_statement.outstanding_expenses || 0) / reportData.income_statement.revenue) * 100).toFixed(1)
                      : '0.0'
                    }%
                  </TableCell>
                </TableRow>

                {/* Net Outstanding */}
                <TableRow>
                  <TableCell 
                    sx={{ 
                      pl: 3, 
                      fontWeight: 'bold',
                      borderTop: '1px solid #e0e0e0',
                      color: 'text.primary'
                    }}
                  >
                    Net Outstanding Amount
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      fontWeight: 'bold',
                      borderTop: '1px solid #e0e0e0',
                      color: ((reportData.income_statement.outstanding_revenue || 0) - (reportData.income_statement.outstanding_expenses || 0)) >= 0 ? 'warning.main' : 'error.main'
                    }}
                  >
                    {formatCurrency((reportData.income_statement.outstanding_revenue || 0) - (reportData.income_statement.outstanding_expenses || 0))}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      fontWeight: 'bold',
                      borderTop: '1px solid #e0e0e0',
                      color: 'text.secondary'
                    }}
                  >
                    {reportData.income_statement.revenue > 0 
                      ? ((((reportData.income_statement.outstanding_revenue || 0) - (reportData.income_statement.outstanding_expenses || 0)) / reportData.income_statement.revenue) * 100).toFixed(1)
                      : '0.0'
                    }%
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderBalanceSheet = () => {
    if (!reportData) return null;

    return (
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Balance Sheet
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                startIcon={<Download size={16} />} 
                variant="outlined" 
                size="small"
                data-export-pdf="balance-sheet"
                onClick={exportBalanceSheetToPDF}
              >
                Export PDF
              </Button>
              <Button startIcon={<Mail size={16} />} variant="outlined" size="small">
                Email
              </Button>
            </Box>
          </Box>

          <div id="balance-sheet-content">
            <Grid container spacing={4}>
            {/* Assets */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                ASSETS
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {reportData.balance_sheet.asset_breakdown.map((item) => (
                      <TableRow key={item.category}>
                        <TableCell>{item.category}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', borderTop: '2px solid #e0e0e0', fontSize: '1.1rem' }}>
                        TOTAL ASSETS
                      </TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ fontWeight: 'bold', borderTop: '2px solid #e0e0e0', fontSize: '1.1rem' }}
                      >
                        {formatCurrency(reportData.balance_sheet.assets)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Liabilities & Equity */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'error.main' }}>
                LIABILITIES
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {reportData.balance_sheet.liability_breakdown.map((item) => (
                      <TableRow key={item.category}>
                        <TableCell>{item.category}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                        Total Liabilities
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                        {formatCurrency(reportData.balance_sheet.liabilities)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, mt: 3, color: 'success.main' }}>
                EQUITY
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Owner's Equity</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(reportData.balance_sheet.equity)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', borderTop: '2px solid #e0e0e0', fontSize: '1.1rem' }}>
                        TOTAL LIABILITIES & EQUITY
                      </TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ fontWeight: 'bold', borderTop: '2px solid #e0e0e0', fontSize: '1.1rem' }}
                      >
                        {formatCurrency(reportData.balance_sheet.liabilities + reportData.balance_sheet.equity)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderCashFlow = () => {
    if (!reportData) return null;

    return (
      <Grid container spacing={3}>
        {/* Cash Flow Summary */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                Cash Flow Summary
              </Typography>
              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        Operating Activities
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(reportData.cash_flow.operating)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                        Investing Activities
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(reportData.cash_flow.investing)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: 'info.main' }}>
                        Financing Activities
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(reportData.cash_flow.financing)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 'bold', 
                          borderTop: '2px solid #e0e0e0',
                          fontSize: '1.1rem',
                          color: reportData.cash_flow.net_cash_flow >= 0 ? 'success.main' : 'error.main'
                        }}
                      >
                        Net Cash Flow
                      </TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          fontWeight: 'bold', 
                          borderTop: '2px solid #e0e0e0',
                          fontSize: '1.1rem',
                          color: reportData.cash_flow.net_cash_flow >= 0 ? 'success.main' : 'error.main'
                        }}
                      >
                        {formatCurrency(reportData.cash_flow.net_cash_flow)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Cash Flow Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                Monthly Cash Flow
              </Typography>
              <Box sx={{ height: 300 }}>
                {ResponsiveContainer && BarChart ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.monthly_trends.map(trend => ({
                      month: trend.month,
                      operating: trend.profit > 0 ? trend.profit : 0,
                      investing: Math.random() * -1000, // Placeholder for investing
                      financing: Math.random() * -500   // Placeholder for financing
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }}
                      />
                      <Bar dataKey="operating" fill="#4caf50" name="Operating" />
                      <Bar dataKey="investing" fill="#ff9800" name="Investing" />
                      <Bar dataKey="financing" fill="#2196f3" name="Financing" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    bgcolor: '#f5f5f5',
                    borderRadius: 2
                  }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Chart visualization unavailable
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // PDF Export functionality
  const exportIncomeStatementToPDF = async () => {
    try {
      const element = document.getElementById('income-statement-content');
      if (!element) {
        console.error('Income statement element not found');
        return;
      }

      // Show loading state
      const button = document.querySelector('[data-export-pdf="income-statement"]') as HTMLButtonElement;
      if (button) {
        button.disabled = true;
        button.textContent = 'Generating PDF...';
      }

      // Convert HTML to canvas
      const canvas = await html2canvas(element, {
        useCORS: true,
        allowTaint: true
      });

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Calculate dimensions
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Add header
      const propertyName = properties.find(p => p.id === selectedPropertyId)?.name || 'Property';
      const reportDate = new Date().toLocaleDateString();
      
      pdf.setFontSize(16);
      pdf.text(`${t('income_statement')} - ${propertyName}`, 20, 20);
      pdf.setFontSize(12);
      pdf.text(`Period: ${dateRange.start_date} to ${dateRange.end_date}`, 20, 30);
      pdf.text(`Generated: ${reportDate}`, 20, 38);

      position = 45; // Start content below header

      // Add the image
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add new pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Save the PDF
      const fileName = `Income_Statement_${propertyName.replace(/\s+/g, '_')}_${dateRange.start_date}_to_${dateRange.end_date}.pdf`;
      pdf.save(fileName);

      // Reset button state
      if (button) {
        button.disabled = false;
        button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Export PDF';
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      
      // Reset button state on error
      const button = document.querySelector('[data-export-pdf="income-statement"]') as HTMLButtonElement;
      if (button) {
        button.disabled = false;
        button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Export PDF';
      }
      
      alert('Error generating PDF. Please try again.');
    }
  };

  // PDF Export functionality for Balance Sheet
  const exportBalanceSheetToPDF = async () => {
    try {
      const element = document.getElementById('balance-sheet-content');
      if (!element) {
        console.error('Balance sheet element not found');
        return;
      }

      // Show loading state
      const button = document.querySelector('[data-export-pdf="balance-sheet"]') as HTMLButtonElement;
      if (button) {
        button.disabled = true;
        button.textContent = 'Generating PDF...';
      }

      // Convert HTML to canvas
      const canvas = await html2canvas(element, {
        useCORS: true,
        allowTaint: true
      });

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Calculate dimensions
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Add header
      const propertyName = properties.find(p => p.id === selectedPropertyId)?.name || 'Property';
      const reportDate = new Date().toLocaleDateString();
      
      pdf.setFontSize(16);
      pdf.text(`Balance Sheet - ${propertyName}`, 20, 20);
      pdf.setFontSize(12);
      pdf.text(`Period: ${dateRange.start_date} to ${dateRange.end_date}`, 20, 30);
      pdf.text(`Generated: ${reportDate}`, 20, 38);

      position = 45; // Start content below header

      // Add the image
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add new pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Save the PDF
      const fileName = `Balance_Sheet_${propertyName.replace(/\s+/g, '_')}_${dateRange.start_date}_to_${dateRange.end_date}.pdf`;
      pdf.save(fileName);

      // Reset button state
      if (button) {
        button.disabled = false;
        button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Export PDF';
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      
      // Reset button state on error
      const button = document.querySelector('[data-export-pdf="balance-sheet"]') as HTMLButtonElement;
      if (button) {
        button.disabled = false;
        button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Export PDF';
      }
      
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Financial Reports
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1 }}>
            Comprehensive financial analysis and reporting
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Filter size={20} />}
          sx={{
            background: 'linear-gradient(135deg, #1976d2, #9c27b0)',
            px: 3,
            py: 1.5,
            borderRadius: 3,
            '&:hover': {
              background: 'linear-gradient(135deg, #1565c0, #7b1fa2)',
            }
          }}
        >
          Export All Reports
        </Button>
      </Box>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', mb: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6} lg={2.5}>
              <FormControl fullWidth>
                <InputLabel>Property</InputLabel>
                <Select
                  value={selectedPropertyId || ''}
                  onChange={(e) => onPropertyChange(e.target.value as number)}
                  label="Property"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { borderRadius: 2 }
                  }}
                >
                  <MenuItem value="">All Properties</MenuItem>
                  {properties.map(property => (
                    <MenuItem key={property.id} value={property.id}>
                      {property.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} lg={2.5}>
              <HierarchicalUnitSelector
                units={availableUnits as any[]}
                selectedPropertyId={selectedPropertyId || ''}
                value={selectedUnitId ? selectedUnitId.toString() : ''}
                onChange={(value) => {
                  const unitId = value ? parseInt(value) : undefined;
                  if (onUnitChange) {
                    onUnitChange(unitId);
                  }
                }}
                label="Unit"
                showAllOption={true}
                disabled={!selectedPropertyId}
              />
            </Grid>
            
            <Grid item xs={12} md={6} lg={2.5}>
              <TextField
                type="date"
                label="Start Date"
                value={dateRange.start_date}
                onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6} lg={2.5}>
              <TextField
                type="date"
                label="End Date"
                value={dateRange.end_date}
                onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6} lg={2}>
              <Button
                onClick={loadReportData}
                variant="contained"
                startIcon={<Filter size={20} />}
                fullWidth
                sx={{
                  height: 56,
                  background: 'linear-gradient(135deg, #1976d2, #3f51b5)',
                  borderRadius: 2,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0, #303f9f)',
                  }
                }}
              >
                Generate Reports
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      {/* Report Tabs */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', mb: 4 }}>
        <Tabs
          value={activeReport}
          onChange={(e, newValue) => setActiveReport(newValue)}
          sx={{
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(135deg, #1976d2, #9c27b0)',
              height: 3,
              borderRadius: 1.5
            }
          }}
        >
          <Tab
            label="Overview"
            value="overview"
            icon={<TrendingUp size={20} />}
            iconPosition="start"
            sx={{ minHeight: 64, px: 3 }}
          />
          <Tab
            label={t('income_statement')}
            value="income"
            icon={<Receipt size={20} />}
            iconPosition="start"
            sx={{ minHeight: 64, px: 3 }}
          />
          <Tab
            label="Balance Sheet"
            value="balance"
            icon={<BarChart3 size={20} />}
            iconPosition="start"
            sx={{ minHeight: 64, px: 3 }}
          />
          <Tab
            label="Cash Flow"
            value="cashflow"
            icon={<DollarSign size={20} />}
            iconPosition="start"
            sx={{ minHeight: 64, px: 3 }}
          />
        </Tabs>
      </Paper>

      {/* Report Content */}
      <Box sx={{ mt: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {activeReport === 'overview' && renderOverview()}
            {activeReport === 'income' && renderIncomeStatement()}
            {activeReport === 'balance' && renderBalanceSheet()}
            {activeReport === 'cashflow' && renderCashFlow()}
          </>
        )}
      </Box>
    </Container>
  );
};

export default FinancialReports;